import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Download, Upload, Palette, RotateCcw, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface CustomTheme {
  name: string;
  colors: {
    background: string;
    foreground: string;
    card: string;
    cardForeground: string;
    popover: string;
    popoverForeground: string;
    primary: string;
    primaryForeground: string;
    secondary: string;
    secondaryForeground: string;
    muted: string;
    mutedForeground: string;
    accent: string;
    accentForeground: string;
    destructive: string;
    destructiveForeground: string;
    border: string;
    input: string;
    ring: string;
    workspace: string;
  };
}

interface ThemeDesignerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULT_THEME: CustomTheme = {
  name: 'My Custom Theme',
  colors: {
    background: '#ffffff',
    foreground: '#0f172a',
    card: '#ffffff',
    cardForeground: '#0f172a',
    popover: '#ffffff',
    popoverForeground: '#0f172a',
    primary: '#3b82f6',
    primaryForeground: '#ffffff',
    secondary: '#f1f5f9',
    secondaryForeground: '#0f172a',
    muted: '#f1f5f9',
    mutedForeground: '#64748b',
    accent: '#f1f5f9',
    accentForeground: '#0f172a',
    destructive: '#ef4444',
    destructiveForeground: '#ffffff',
    border: '#e2e8f0',
    input: '#e2e8f0',
    ring: '#3b82f6',
    workspace: '#f8fafc',
  },
};

const COLOR_LABELS: Record<keyof CustomTheme['colors'], string> = {
  background: 'Background',
  foreground: 'Text',
  card: 'Card Background',
  cardForeground: 'Card Text',
  popover: 'Popover Background',
  popoverForeground: 'Popover Text',
  primary: 'Primary',
  primaryForeground: 'Primary Text',
  secondary: 'Secondary',
  secondaryForeground: 'Secondary Text',
  muted: 'Muted',
  mutedForeground: 'Muted Text',
  accent: 'Accent',
  accentForeground: 'Accent Text',
  destructive: 'Destructive',
  destructiveForeground: 'Destructive Text',
  border: 'Border',
  input: 'Input Border',
  ring: 'Focus Ring',
  workspace: 'Workspace',
};

const hexToHsl = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0 0% 0%';
  
  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const hslToHex = (hsl: string): string => {
  const [h, s, l] = hsl.split(' ').map((v, i) => {
    if (i === 0) return parseFloat(v) / 360;
    return parseFloat(v.replace('%', '')) / 100;
  });
  
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const ThemeDesigner: React.FC<ThemeDesignerProps> = ({ open, onOpenChange }) => {
  const [theme, setTheme] = useState<CustomTheme>(() => {
    const stored = localStorage.getItem('customTheme');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return DEFAULT_THEME;
      }
    }
    return DEFAULT_THEME;
  });
  const [jsonInput, setJsonInput] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setJsonInput(JSON.stringify(theme, null, 2));
  }, [theme]);

  const updateColor = (key: keyof CustomTheme['colors'], value: string) => {
    setTheme(prev => ({
      ...prev,
      colors: { ...prev.colors, [key]: value }
    }));
  };

  const applyTheme = () => {
    const root = document.documentElement;
    
    // Apply as custom theme
    Object.entries(theme.colors).forEach(([key, value]) => {
      const cssVar = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      root.style.setProperty(`--${cssVar}`, hexToHsl(value));
    });
    
    localStorage.setItem('customTheme', JSON.stringify(theme));
    localStorage.setItem('theme', 'custom');
    root.classList.add('custom');
    toast.success('Theme applied!');
  };

  const resetTheme = () => {
    setTheme(DEFAULT_THEME);
    toast.info('Theme reset to defaults');
  };

  const exportTheme = () => {
    const blob = new Blob([JSON.stringify(theme, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${theme.name.toLowerCase().replace(/\s+/g, '-')}-theme.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Theme exported!');
  };

  const importTheme = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target?.result as string);
        if (imported.colors && imported.name) {
          setTheme(imported);
          toast.success('Theme imported!');
        } else {
          toast.error('Invalid theme file');
        }
      } catch {
        toast.error('Failed to parse theme file');
      }
    };
    reader.readAsText(file);
  };

  const importFromJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      if (parsed.colors && parsed.name) {
        setTheme(parsed);
        toast.success('Theme loaded from JSON!');
      } else {
        toast.error('Invalid theme format');
      }
    } catch {
      toast.error('Invalid JSON');
    }
  };

  const copyJson = async () => {
    await navigator.clipboard.writeText(JSON.stringify(theme, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Theme copied to clipboard!');
  };

  const colorGroups = [
    { name: 'Base', keys: ['background', 'foreground', 'workspace'] },
    { name: 'Cards & Popovers', keys: ['card', 'cardForeground', 'popover', 'popoverForeground'] },
    { name: 'Primary & Secondary', keys: ['primary', 'primaryForeground', 'secondary', 'secondaryForeground'] },
    { name: 'Muted & Accent', keys: ['muted', 'mutedForeground', 'accent', 'accentForeground'] },
    { name: 'States', keys: ['destructive', 'destructiveForeground', 'border', 'input', 'ring'] },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Theme Designer
          </DialogTitle>
          <DialogDescription>
            Create your own custom color theme
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="visual" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="shrink-0">
            <TabsTrigger value="visual">Visual Editor</TabsTrigger>
            <TabsTrigger value="json">JSON</TabsTrigger>
          </TabsList>
          
          <TabsContent value="visual" className="flex-1 overflow-hidden m-0 mt-4">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm">Theme Name</Label>
                <Input
                  value={theme.name}
                  onChange={(e) => setTheme(prev => ({ ...prev, name: e.target.value }))}
                  className="h-8 max-w-48"
                  placeholder="My Custom Theme"
                />
              </div>
              
              <ScrollArea className="h-[340px] pr-4">
                <div className="space-y-6">
                  {colorGroups.map(group => (
                    <div key={group.name} className="space-y-3">
                      <h4 className="text-sm font-medium text-muted-foreground">{group.name}</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {group.keys.map(key => (
                          <div key={key} className="flex items-center gap-2">
                            <div className="relative">
                              <input
                                type="color"
                                value={theme.colors[key as keyof CustomTheme['colors']]}
                                onChange={(e) => updateColor(key as keyof CustomTheme['colors'], e.target.value)}
                                className="w-8 h-8 rounded cursor-pointer border border-border"
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <Label className="text-xs truncate block">
                                {COLOR_LABELS[key as keyof CustomTheme['colors']]}
                              </Label>
                              <Input
                                value={theme.colors[key as keyof CustomTheme['colors']]}
                                onChange={(e) => updateColor(key as keyof CustomTheme['colors'], e.target.value)}
                                className="h-6 text-[10px] font-mono px-1"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          </TabsContent>
          
          <TabsContent value="json" className="flex-1 overflow-hidden m-0 mt-4 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <Button variant="outline" size="sm" onClick={importFromJson} className="gap-1.5">
                <Upload className="w-3.5 h-3.5" />
                Load
              </Button>
              <Button variant="outline" size="sm" onClick={copyJson} className="gap-1.5">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                Copy
              </Button>
            </div>
            <Textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              className="flex-1 font-mono text-xs resize-none"
              placeholder="Paste theme JSON here..."
            />
          </TabsContent>
        </Tabs>

        {/* Preview */}
        <div className="border rounded-lg p-4 mt-4 shrink-0" style={{
          backgroundColor: theme.colors.background,
          color: theme.colors.foreground,
          borderColor: theme.colors.border,
        }}>
          <p className="text-xs font-medium mb-2">Preview</p>
          <div className="flex items-center gap-2">
            <div className="px-3 py-1 rounded text-xs" style={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.primaryForeground,
            }}>
              Primary
            </div>
            <div className="px-3 py-1 rounded text-xs" style={{
              backgroundColor: theme.colors.secondary,
              color: theme.colors.secondaryForeground,
            }}>
              Secondary
            </div>
            <div className="px-3 py-1 rounded text-xs" style={{
              backgroundColor: theme.colors.muted,
              color: theme.colors.mutedForeground,
            }}>
              Muted
            </div>
            <div className="px-3 py-1 rounded text-xs" style={{
              backgroundColor: theme.colors.destructive,
              color: theme.colors.destructiveForeground,
            }}>
              Destructive
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 gap-2 flex-wrap">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={resetTheme} className="gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" />
              Reset
            </Button>
            <label>
              <Button variant="outline" size="sm" asChild className="gap-1.5 cursor-pointer">
                <span>
                  <Upload className="w-3.5 h-3.5" />
                  Import
                </span>
              </Button>
              <input type="file" accept=".json" onChange={importTheme} className="hidden" />
            </label>
            <Button variant="outline" size="sm" onClick={exportTheme} className="gap-1.5">
              <Download className="w-3.5 h-3.5" />
              Export
            </Button>
          </div>
          <Button onClick={applyTheme} className="gap-1.5">
            Apply Theme
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
